import { useCallback, useMemo } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import {
  Skeleton,
  Typography,
  TypographyVariant,
  TypographyWeight,
} from 'src/components/atoms'
import {
  IFilterDropdownOption,
  Pagination,
  SearchableFilterDropdown,
  SearchBar,
  SkeletonWrapper,
} from 'src/components/molecules'
import { Table } from 'src/components/organisms'
import {
  Data,
  type ITableEmptyState,
} from 'src/components/organisms/table/types'

import { PortfolioNewsMobileList } from './components/portfolio-news-mobile-list'
import { usePortfolioNewsTableColumns } from './hooks/use-portfolio-news-table-columns'
import { IPortfolioNewsArticleEntity, IUsePortfolioNewsReturn } from './types'

const asArticleRow = (row: Data): IPortfolioNewsArticleEntity =>
  row as unknown as IPortfolioNewsArticleEntity

const COMPANY_FILTER_ALL = 'all'

interface IPortfolioNewsTemplateProps {
  isLoading: boolean
  state: IUsePortfolioNewsReturn
}

const NewsSectionSkeleton = (): JSX.Element => (
  <div className="flex flex-col gap-7">
    <Skeleton.NewsTable rows={6} />
  </div>
)

export const PortfolioNewsTemplate = ({
  isLoading,
  state,
}: IPortfolioNewsTemplateProps): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const newsTableColumns = usePortfolioNewsTableColumns()

  const {
    searchQuery,
    companyFilter,
    currentPage,
    totalPages,
    pageItems,
    companies,
    isError,
    isPaginationError,
    isEmpty,
    setSearchQuery,
    setCompanyFilter,
    setCurrentPage,
  } = state

  const hasActiveFilters =
    searchQuery.trim() !== '' || companyFilter !== COMPANY_FILTER_ALL

  const tableEmptyState = useMemo((): ITableEmptyState | undefined => {
    if (!isEmpty) {
      return undefined
    }
    return {
      title: hasActiveFilters
        ? t('portfolioNews.filters.noResultsFound')
        : t('portfolioNews.filters.emptyTitle'),
      description: hasActiveFilters
        ? t('portfolioNews.filters.noResultsFoundDescription')
        : t('portfolioNews.filters.emptyDescription'),
    }
  }, [hasActiveFilters, isEmpty, t])

  const mobileEmptyMessage = hasActiveFilters
    ? t('portfolioNews.filters.noResultsFoundDescription')
    : t('portfolioNews.filters.emptyDescription')

  const companyOptions = useMemo((): IFilterDropdownOption[] => {
    const fromItems = companies.map(company => ({
      value: company,
      label: company,
    }))
    const selectedNotListed =
      companyFilter !== COMPANY_FILTER_ALL && !companies.includes(companyFilter)
        ? [{ value: companyFilter, label: companyFilter }]
        : []

    return [
      {
        value: COMPANY_FILTER_ALL,
        label: t('portfolioNews.filters.allCompanies'),
        triggerLabel: t('portfolioNews.filters.filterByCompany'),
      },
      ...fromItems,
      ...selectedNotListed,
    ]
  }, [companies, companyFilter, t])

  const handleArticleNavigate = useCallback(
    (article: IPortfolioNewsArticleEntity): void => {
      navigate(`/portfolio-news/${article.id}`)
    },
    [navigate],
  )

  return (
    <main className="bg-surface-page">
      <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-10 px-6 py-11 md:px-12 xl:px-0">
        <header className="flex flex-col gap-4">
          <Typography
            variant={TypographyVariant.headline}
            weight={TypographyWeight.light}
            className="text-content-primary"
          >
            {t('portfolioNews.pageTitle')}
          </Typography>
          <div className="h-px w-full bg-content-secondary" />
        </header>

        <section className="flex flex-col gap-10">
          <div className="flex h-11 w-full items-center gap-2 md:gap-4">
            <SearchBar
              className="flex-1"
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder={t('portfolioNews.search.placeholder')}
              name="portfolio-news-search"
            />
            <SearchableFilterDropdown
              name="portfolio-news-company-filter"
              value={companyFilter}
              defaultValue={COMPANY_FILTER_ALL}
              placeholder={t('portfolioNews.filters.filterByCompany')}
              onChange={setCompanyFilter}
              options={companyOptions}
              className="w-[125px] md:w-[160px]"
            />
          </div>

          {(!isError || isPaginationError) && (
            <SkeletonWrapper
              isLoading={isLoading}
              skeleton={<NewsSectionSkeleton />}
            >
              <div className="flex w-full flex-col items-end gap-6">
                {/* Mobile shows the stacked article list, per Member Insights pattern. */}
                <div className="w-full md:hidden">
                  {isEmpty ? (
                    <div className="py-10 text-center">
                      <Typography
                        variant={TypographyVariant.bodyXs}
                        className="text-content-secondary"
                      >
                        {mobileEmptyMessage}
                      </Typography>
                    </div>
                  ) : (
                    <PortfolioNewsMobileList
                      articles={pageItems}
                      onArticleClick={handleArticleNavigate}
                    />
                  )}
                </div>

                <div className="hidden w-full overflow-x-auto md:block">
                  <Table
                    columns={newsTableColumns}
                    data={pageItems as unknown as Data[]}
                    emptyState={tableEmptyState}
                    onRowClick={row => {
                      const item = asArticleRow(row)
                      navigate(`/portfolio-news/${item.id}`)
                    }}
                    idPropertyName="id"
                  />
                </div>

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onChange={setCurrentPage}
                />

                {isPaginationError && (
                  <Typography
                    variant={TypographyVariant.bodyXs}
                    className="w-full text-center text-content-negative"
                  >
                    {t('portfolioNews.pagination.error')}
                  </Typography>
                )}
              </div>
            </SkeletonWrapper>
          )}
        </section>
      </div>
    </main>
  )
}
